import { type FC, useCallback, useEffect, useState } from "react"

import {
	type LandingPageTextBlock,
	type LandingPageTextSize,
	landingPageTextSizeLabels,
} from "@forento/core/models/landing"
import { landingPageCopyCharacterLimits, landingPageTextSizes } from "@forento/core/schemas/landing"
import { landingPageTextSizeClasses } from "@forento/ui/utilities/landingTextSize"
import { cn } from "@forento/ui/utilities/tailwind"

import EditorShell from "./EditorShell"
import RichEditableField from "./RichEditableField"

type Props = {
	block: LandingPageTextBlock
	onChange: (block: LandingPageTextBlock) => void
	onClose: () => void
}
const TextBlockEditor: FC<Props> = ({ block, onChange, onClose }) => {
	const [text, setText] = useState(block.content.text)
	const [size, setSize] = useState<LandingPageTextSize>(block.content.size)

	useEffect(() => {
		onChange({
			type: "text",
			content: { text, size },
		})
	}, [onChange, size, text])

	const handleChange = useCallback((value: string | null) => {
		setText(value ?? "")
	}, [])

	return (
		<EditorShell onClose={onClose}>
			<section className="px-6 pt-20 pb-8 sm:pt-24 lg:px-8">
				<div className="mb-6 flex justify-center">
					<div className="flex flex-wrap items-center justify-center gap-2 rounded-full border border-black/10 bg-white/95 p-1 shadow-sm">
						{landingPageTextSizes.map(option => (
							<button
								key={option}
								type="button"
								onClick={() => setSize(option)}
								className={cn(
									"cursor-pointer rounded-full px-4 py-2 text-sm font-medium transition-colors",
									size === option ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100",
								)}
							>
								{landingPageTextSizeLabels[option]}
							</button>
						))}
					</div>
				</div>
				<div className="mx-auto max-w-3xl">
					<RichEditableField
						initialValue={block.content.text}
						onChange={handleChange}
						className="min-h-48"
						contentClassName={landingPageTextSizeClasses[size]}
						maxCharacters={landingPageCopyCharacterLimits.body}
					/>
				</div>
			</section>
		</EditorShell>
	)
}

export default TextBlockEditor
